"use client"

import { useEffect, useState } from "react"
import TheGradient from "@/components/TheGradient"
import { resolveThemeColorVarHex } from "@/lib/theme/color"

type GradientColors = {
  primary: string
  secondary: string
  accent: string
}

const fallbackGradientColors: GradientColors = {
  primary: "#FF9FFC",
  secondary: "#5227FF",
  accent: "#B19EEF",
}

const resolveGradientColors = (): GradientColors => {
  if (typeof document === "undefined") {
    return fallbackGradientColors
  }

  return {
    primary: resolveThemeColorVarHex(
      "--color-primary",
      fallbackGradientColors.primary,
    ),
    secondary: resolveThemeColorVarHex(
      "--color-secondary",
      fallbackGradientColors.secondary,
    ),
    accent: resolveThemeColorVarHex(
      "--color-accent",
      fallbackGradientColors.accent,
    ),
  }
}

export default function AuthThemeGradient() {
  const [colors, setColors] = useState<GradientColors>(() => {
    if (typeof document === "undefined") {
      return fallbackGradientColors
    }
    return resolveGradientColors()
  })

  useEffect(() => {
    const updateColors = () => {
      const nextColors = resolveGradientColors()
      setColors((previousColors) => {
        if (
          previousColors.primary === nextColors.primary &&
          previousColors.secondary === nextColors.secondary &&
          previousColors.accent === nextColors.accent
        ) {
          return previousColors
        }
        return nextColors
      })
    }

    updateColors()

    const observer = new MutationObserver(updateColors)
    const observeConfig: MutationObserverInit = {
      attributes: true,
      attributeFilter: ["data-theme", "class", "style"],
    }

    observer.observe(document.documentElement, observeConfig)
    if (document.body) {
      observer.observe(document.body, observeConfig)
    }

    return () => observer.disconnect()
  }, [])

  return (
    <TheGradient
      className="fixed inset-0 h-screen w-screen pointer-events-none"
      color1={colors.primary}
      color2={colors.secondary}
      color3={colors.accent}
      timeSpeed={0.35}
      colorBalance={0}
      warpStrength={1.5}
      warpFrequency={5}
      warpSpeed={6}
      warpAmplitude={50}
      blendAngle={0}
      blendSoftness={0.1}
      rotationAmount={500}
      noiseScale={1.5}
      grainAmount={0.15}
      grainScale={2}
      grainAnimated={false}
      contrast={1.7}
      gamma={0.8}
      saturation={1}
      centerX={0}
      centerY={0}
      zoom={0.9}
    />
  )
}
